import { GetStaticPaths, GetStaticProps } from 'next';
import { Fragment } from 'react';
import dynamic from 'next/dynamic';
import slugify from 'slugify';

import client from '@utils/apolloClient';
import {
  countFeatureItems,
  getFeatureItemBySlug,
  getFeatureSlugs,
} from '@queries/features.graphql';
import { getProductItemsById } from '@queries/products.graphql';

import getAllStorySlugs from '@utils/getAllStorySlugs';
import getAvailabilty from '@utils/getAvailability';
import getEntryBoxInfo from '@utils/getEntryBoxInfo';
import getFullUrl from '@utils/getFullUrl';
import getImagePath from '@utils/getImagePath';
import getStoreInfo from '@utils/getStoreInfo';
import getStructuredData from '@utils/getStructuredData';
import replaceImageHost from '@utils/replaceImageHost';
import sortBy from '@utils/sortBy';

import Article from '@components/Article/Article';
import Layout from '@components/Layout/Layout';
import MetaDetails from '@components/MetaDetails/MetaDetails';
import { TagList } from '@components/Tags/Tags';

const AffliateMessage = dynamic(
  () => import('@components/AffiliateMessage/AffliateMessage')
);
const ContentList = dynamic(
  () => import('@components/ContentList/ContentList')
);
const Description = dynamic(
  () => import('@components/Description/Description')
);
const Hero = dynamic(() => import('@components/Hero/Hero'));
const ProductInfoBox = dynamic(
  () => import('@components/ProductInfoBox/ProductInfoBox')
);
const ProductCountSummary = dynamic(
  () => import('@components/ProductCountSummary/ProductCountSummary')
);
const SecondaryTitle = dynamic(
  () => import('@components/SecondaryTitle/SecondaryTitle')
);
const Social = dynamic(() => import('@components/Social/Social'));
const Strapline = dynamic(() => import('@components/Strapline/Strapline'));
const Tags = dynamic(() => import('@components/Tags/Tags'));
const Title = dynamic(() => import('@components/Title/Title'));

type FeaturesPostContent = {
  conclusion: string;
  introduction: string;
  meta_description: string;
  pageTitle: string;
  show_breakdown: boolean;
  show_positions: string;
  strapline: string;
};

type FeaturesProps = {
  content: FeaturesPostContent;
  conclusion: string;
  date: string;
  featureFlags: any;
  introduction: string;
  latest: any;
  products?: any;
  schema: string;
  show_positions: string;
  siteDescription: string;
  siteTitle: string;
  siteUrl: string;
  slug: string;
  tags?: TagList;
  title: string;
};

const Feature = ({
  content,
  date,
  featureFlags,
  latest,
  products,
  schema,
  siteDescription,
  siteTitle,
  siteUrl,
  slug,
  tags,
  title,
}: FeaturesProps) => {
  const {
    conclusion,
    introduction,
    meta_description,
    pageTitle,
    show_breakdown,
    show_positions,
    strapline,
  } = content;
  const section = 'Features';

  const url = getFullUrl(section, slug);

  return (
    <Layout
      featureFlags={featureFlags}
      pageDescription={meta_description || siteDescription}
      pageTitle={pageTitle}
      pageType="article"
      schema={schema}
      section={section}
      siteTitle={siteTitle}
      url={url}
    >
      <Article>
        <Article.HeaderWrapper>
          <Title>{title}</Title>
          <Strapline text={strapline} />
        </Article.HeaderWrapper>

        <Article.ContentWrapper>
          <MetaDetails
            dt={date}
            dtLabel="Updated"
            section={section}
          ></MetaDetails>
          {introduction ? (
            <Description content={introduction} leadingLetter={true} />
          ) : null}
        </Article.ContentWrapper>

        {products ? (
          <>
            <AffliateMessage />
            <div>
              {products.map((item, index) => {
                const brand = JSON.parse(item.product.content.brand) || {};
                const releases = item.product.content.releases || [];
                const stores = item.product.content.stores || [];

                const releaseStatus = getAvailabilty(releases);

                return (
                  <Fragment key={item.title || item.product.title}>
                    <ProductInfoBox
                      align={index % 2 === 0 ? 'left' : 'right'}
                      brand={brand.title}
                      title={item.title || item.product.title}
                      description={item.description}
                      reference={item.product.content.ref}
                      referenceName={brand.ref}
                      parts={item.product.content.parts?.toString()}
                      partsName={brand.part}
                      position={
                        show_positions !== 'none'
                          ? show_positions === 'forward'
                            ? 1 + index
                            : products.length - index
                          : undefined
                      }
                      figures={item.product.content.figures?.toString()}
                      figuresName={brand.figure}
                      image={item.product.content.images[0]}
                      releaseStatus={releaseStatus}
                      section={section}
                      showPurchase={!item.hide_purchase_options}
                      stores={getStoreInfo(stores)}
                    />
                    {item.hero && item.hero.length > 0 ? (
                      <Hero
                        image={item.hero.image}
                        key={item.hero.title}
                        subtitle={item.hero.subtitle}
                        title={item.hero.title}
                        url={`/${item.hero.url}`}
                      />
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
          </>
        ) : null}

        {conclusion ? (
          <Article.ContentWrapper>
            <Description content={conclusion} />
            {show_breakdown ? (
              <ProductCountSummary products={products} />
            ) : null}
          </Article.ContentWrapper>
        ) : null}

        <Article.ContentWrapper>
          <Social title={title} url={url} />
          {tags ? <Tags tags={tags} /> : null}
        </Article.ContentWrapper>

        <SecondaryTitle>Latest Features</SecondaryTitle>
        <ContentList limit={true} posts={latest || []} />
      </Article>
    </Layout>
  );
};

export default Feature;

export const getStaticProps: GetStaticProps = async ({ ...ctx }) => {
  const { feature: slug } = ctx.params as { feature: string };

  const configData = await import(`../../siteconfig.json`);

  const {
    data: { FeatureItem: feature, FeatureItems: latest },
  } = await client.query({
    query: getFeatureItemBySlug,
    variables: {
      slug: `features/${slug}`,
    },
  });

  if (!feature) {
    throw new Error('Not found');
  }

  let productDetails = [];

  if (feature.content.products && feature.content.products.length > 0) {
    const productIds = feature.content.products.map((item) => item.product);

    const {
      data: { ProductItems: products },
    } = await client.query({
      query: getProductItemsById,
      variables: {
        uuids: productIds.join(','),
      },
    });

    productDetails = feature.content.products.map((item) => {
      if (item.hero && item.hero.length > 0) {
        const heroImage = item.hero[0].image?.filename;

        const blurHeroImage = getImagePath({
          dimensions: '6x6',
          quality: 75,
          url: heroImage,
        });
        const fullHeroImage = getImagePath({
          url: heroImage,
        });

        return {
          ...item,
          hero: {
            ...item.hero[0],
            url: item.hero[0].link.url || item.hero[0].link.cached_url,
            image: {
              blur: blurHeroImage,
              full: fullHeroImage,
            },
          },
          product: products.items.find(
            (product) => product.uuid === item.product
          ),
        };
      }

      return {
        ...item,
        product: products.items.find(
          (product) => product.uuid === item.product
        ),
      };
    });
  }

  const schema = getStructuredData({
    title: feature.title,
    date: feature.date,
    image: feature.content.hero.url,
  });

  const pageTitle = feature.content.show_updated_title
    ? `${feature.title} - Updated for ${new Date(
        feature.date
      ).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
      })}`
    : feature.title;

  return {
    props: {
      ...feature,
      content: {
        ...feature.content,
        conclusion: replaceImageHost(feature.content.conclusion),
        hero: null,
        introduction: replaceImageHost(feature.content.introduction),
        pageTitle,
      },
      featureFlags: configData.featureFlags,
      latest: latest.items
        .filter((item) => item.slug !== feature.slug)
        .map((item) => getEntryBoxInfo(item, 'Features')),
      products: productDetails,
      schema,
      siteDescription: configData.default.description,
      siteTitle: configData.default.title,
      siteUrl: configData.url,
      tags: sortBy(
        'name',
        feature.tag_list.map((tag) => ({
          name: tag,
          slug: slugify(tag, { lower: true }),
        }))
      ),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  type Features = Array<{
    slug: string;
  }>;

  const features: Features = await getAllStorySlugs(
    'FeatureItems',
    countFeatureItems,
    getFeatureSlugs
  );

  const paths =
    features.length > 0
      ? features.map((feature) => `/features/${feature.slug}`)
      : [];

  return {
    paths,
    fallback: false,
  };
};
